// FleetMapPage – fleet device map (dock sheet or fullscreen). Optional lat/lng in URL for map center.
// Plots devices with valid lat/lng; radius filter and map selection integrate with the device list.

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import type { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import DeviceList from '../components/DeviceListView/panels/DeviceListPage';
import { showToastError } from '../helpers/toastError';
import { useDeviceListLiveData } from '../components/DeviceListView/utils/useDeviceListLiveData';
import type { DeviceStatus } from '../../types/DeviceListTypes';
import { ChevronDown } from 'lucide-react';
import { resolveApproximateNetworkLocation } from '../helpers/location';
import { useMapDock } from '../context/MapDockContext';

// ---- Constants ----

const SIDEBAR_MIN_WIDTH = 260;
const DRAG_THRESHOLD_PX = 3;

// Read lazily (not as a module-level const): in the web build, window.fmPublicEnv is
// populated asynchronously by setupWebGlobals() after this module is first imported, so a
// frozen top-level const would permanently capture an empty token. Electron's preload
// exposes it synchronously, which is why this bug was web-only.
function getMapboxEnv(): { accessToken: string; styleIdRaw: string } {
  const rendererEnv = (window.fmPublicEnv ?? {}) as Record<string, string | undefined>;
  return {
    accessToken: (rendererEnv.MAPBOX_ACCESS_TOKEN || '').trim(),
    styleIdRaw: (rendererEnv.MAPBOX_STYLE_ID || 'mapbox/streets-v12').trim(),
  };
}

function normalizeMapboxStyleId(styleId: string): string {
  const prefix = 'mapbox://styles/';
  if (styleId.startsWith(prefix)) {
    return styleId.slice(prefix.length);
  }
  return styleId;
}

function resolveMapboxStyleId(envStyleRaw: string, resolvedTheme: 'light' | 'dark'): string {
  const normalized = normalizeMapboxStyleId(envStyleRaw || 'mapbox/streets-v12');
  const envOverride = envStyleRaw.trim();
  if (
    envOverride &&
    envOverride !== 'mapbox/streets-v12' &&
    envOverride !== 'mapbox/dark-v11' &&
    !envOverride.endsWith('/streets-v12') &&
    !envOverride.endsWith('/dark-v11')
  ) {
    return normalized;
  }
  return resolvedTheme === 'dark' ? 'mapbox/dark-v11' : 'mapbox/streets-v12';
}

const TILE_ATTRIBUTION =
  '© <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noreferrer">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>';
const MIN_RADIUS_KM = 0.01; // 10 meters
const MAX_RADIUS_KM = 100;
const RADIUS_SLIDER_STEPS = 1000;

function radiusKmToSliderValue(radiusKm: number): number {
  const clamped = Math.min(Math.max(radiusKm, MIN_RADIUS_KM), MAX_RADIUS_KM);
  const logMin = Math.log(MIN_RADIUS_KM);
  const logMax = Math.log(MAX_RADIUS_KM);
  const normalized = (Math.log(clamped) - logMin) / (logMax - logMin);
  return Math.round(normalized * RADIUS_SLIDER_STEPS);
}

function sliderValueToRadiusKm(sliderValue: number): number {
  const clamped = Math.min(Math.max(sliderValue, 0), RADIUS_SLIDER_STEPS);
  const normalized = clamped / RADIUS_SLIDER_STEPS;
  const logMin = Math.log(MIN_RADIUS_KM);
  const logMax = Math.log(MAX_RADIUS_KM);
  return Math.exp(logMin + normalized * (logMax - logMin));
}




const DefaultIcon = L.icon({
  iconUrl,
  shadowUrl: iconShadow,
});
L.Marker.prototype.options.icon = DefaultIcon;

function toEpochMs(value?: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  return value > 1e12 ? value : value * 1000;
}

/** Age of the last *location* fix only — do not use general `updatedAt` or dots look “fresh” with stale/missing GPS time. */
function getLocationAgeMs(status?: DeviceStatus): number | null {
  if (!status) return null;
  const locationMs = toEpochMs(status.last_location_updated_at);
  if (locationMs <= 0) return null;
  return Math.max(0, Date.now() - locationMs);
}

const AGE_MS_30S = 30_000;
const AGE_MS_5M = 5 * 60 * 1000;
const AGE_MS_2H = 2 * 60 * 60 * 1000;
const AGE_MS_1D = 24 * 60 * 60 * 1000;

type LocationAgeBucket = 'fresh' | 'recent' | 'stale' | 'old' | 'older' | 'unknown';

function getAgeBucket(ageMs: number | null): LocationAgeBucket {
  if (ageMs == null) return 'unknown';
  if (ageMs < AGE_MS_30S) return 'fresh';
  if (ageMs < AGE_MS_5M) return 'recent';
  if (ageMs < AGE_MS_2H) return 'stale';
  if (ageMs < AGE_MS_1D) return 'old';
  return 'older';
}

function markerColorForAgeBucket(bucket: LocationAgeBucket): string {
  if (bucket === 'fresh') return '#16a34a';
  if (bucket === 'recent') return '#ca8a04';
  if (bucket === 'stale') return '#ea580c';
  if (bucket === 'old') return '#dc2626';
  if (bucket === 'older') return '#7f1d1d';
  return '#6b7280';
}

const LOCATION_AGE_LEGEND: { bucket: LocationAgeBucket; title: string; range: string }[] = [
  { bucket: 'fresh', title: 'Fresh', range: '< 30 s' },
  { bucket: 'recent', title: 'Recent', range: '30 s – < 5 min' },
  { bucket: 'stale', title: 'Stale', range: '5 min – < 2 hr' },
  { bucket: 'old', title: 'Old', range: '2 hr – < 1 day' },
  { bucket: 'older', title: 'Older', range: '≥ 1 day' },
  { bucket: 'unknown', title: 'Unknown', range: 'No fix time' },
];

function ageBucketCaption(bucket: LocationAgeBucket): string {
  const row = LOCATION_AGE_LEGEND.find((r) => r.bucket === bucket);
  return row ? `${row.title} — ${row.range}` : bucket;
}

function buildAgeMarkerIcon(color: string, selected: boolean): L.DivIcon {
  const ring = selected ? '0 0 0 3px rgba(37,99,235,0.35)' : '0 0 0 2px rgba(255,255,255,0.9)';
  return L.divIcon({
    className: '',
    html: `<span style="display:block;width:14px;height:14px;border-radius:9999px;background:${color};box-shadow:${ring};"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
}

/** Fit map bounds to include all positions once when GGA markers first appear (so map doesn’t keep jumping). */
function FitBounds({ positions }: { positions: LatLngTuple[] }) {
  const map = useMap();
  const hasFitted = useRef(false);
  useEffect(() => {
    if (positions.length === 0) {
      hasFitted.current = false;
      return;
    }
    if (hasFitted.current) return;
    hasFitted.current = true;
    if (positions.length === 1) {
      map.setView(positions[0], Math.max(map.getZoom(), 12));
      return;
    }
    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
  }, [map, positions]);
  return null;
}

/** When URL has lat/lng, keep map view centered on them (e.g. after clicking Location in device list). */
function SyncMapToUrlCenter({ center }: { center: LatLngTuple }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, Math.max(map.getZoom(), 12));
  }, [map, center[0], center[1]]);
  return null;
}

/** Pan/zoom map to a device position (e.g. after Device ID search “Focus”). */
function FlyToPosition({ target, nonce }: { target: LatLngTuple; nonce: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(target, Math.max(map.getZoom(), 14), { animate: true });
  }, [map, target[0], target[1], nonce]);
  return null;
}

/** Leaflet needs a resize after dock height / layout changes */
function InvalidateMapSize({ revision }: { revision: number }) {
  const map = useMap();
  useEffect(() => {
    const t = window.setTimeout(() => {
      map.invalidateSize();
    }, 320);
    return () => window.clearTimeout(t);
  }, [map, revision]);

  useEffect(() => {
    const onResize = () => map.invalidateSize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [map]);

  return null;
}

import type { FleetMapPageProps } from './types/PageProps';

export type { FleetMapPageProps };

// ---- Component ----

export default function FleetMapPage({
  operatorScopedEventId = null,
  operatorScopedEventName = null,
  operatorScopedRaceId = null,
  variant = 'page',
  layoutRevision = 0,
}: FleetMapPageProps) {
  const { resolvedTheme } = useTheme();
  const { accessToken: mapboxAccessToken, styleIdRaw: mapboxStyleIdRaw } = useMemo(
    () => getMapboxEnv(),
    [],
  );
  const mapboxStyleId = useMemo(
    () => resolveMapboxStyleId(mapboxStyleIdRaw, resolvedTheme),
    [mapboxStyleIdRaw, resolvedTheme],
  );
  const tileLayerUrl = useMemo(
    () =>
      `https://api.mapbox.com/styles/v1/${mapboxStyleId}/tiles/{z}/{x}/{y}?access_token=${mapboxAccessToken}`,
    [mapboxStyleId, mapboxAccessToken],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    mapQuery,
    setMapQuery,
    selectedDeviceIds,
    toggleSelectedDeviceId,
    clearSelectedDeviceIds,
    radiusKm,
    setRadiusKm,
    radiusFilterEnabled,
    setRadiusFilterEnabled,
    radiusCenter,
    setRadiusCenter,
    baseVisibleDeviceIds,
  } = useMapDock();

  const queryString = variant === 'dock' ? mapQuery : searchParams.toString();

  const applyMapSearchParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      if (variant === 'dock') {
        const p = new URLSearchParams(mapQuery);
        mutate(p);
        setMapQuery(p.toString());
        return;
      }
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        mutate(p);
        return p;
      });
    },
    [variant, mapQuery, setMapQuery, setSearchParams],
  );

  const liveDeviceScopeIds = useMemo(
    () => (baseVisibleDeviceIds.length > 0 ? baseVisibleDeviceIds : undefined),
    [baseVisibleDeviceIds],
  );
  const { statusMap } = useDeviceListLiveData({
    deviceIds: liveDeviceScopeIds,
    operatorScopedEventId: operatorScopedEventId ?? null,
    enableInitialStatusQuery: true,
    enableInitialCommandQuery: false,
  });
  const [networkLocateBusy, setNetworkLocateBusy] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  /** Substring filter on `deviceId` for map markers (case-insensitive). */
  const [mapDeviceIdSearch, setMapDeviceIdSearch] = useState('');
  const [flyTo, setFlyTo] = useState<{ position: LatLngTuple; nonce: number } | null>(null);
  const flyToNonceRef = useRef(0);
  /** How the radius circle center was chosen (for labels only). */
  const [radiusCenterSource, setRadiusCenterSource] = useState<'network' | 'manual' | null>(null);
  const [mapControlsOpen, setMapControlsOpen] = useState(true);
  const [visibleAgeBuckets, setVisibleAgeBuckets] = useState<LocationAgeBucket[]>(
    LOCATION_AGE_LEGEND.map(({ bucket }) => bucket),
  );

  useEffect(() => {
    if (!radiusCenter) setRadiusCenterSource(null);
  }, [radiusCenter]);

  /** Devices with valid lat/lng from status (live websocket + initial seeded query). */
  const deviceMarkers = useMemo(() => {
    const out: { deviceId: string; lat: number; lng: number; ageBucket: LocationAgeBucket }[] = [];

    if (statusMap && typeof statusMap === 'object') {
      for (const [id, status] of Object.entries(statusMap as Record<string, DeviceStatus>)) {
        const slat = status?.lat;
        const slng = status?.lng;
        if (typeof slat === 'number' && typeof slng === 'number' && !Number.isNaN(slat) && !Number.isNaN(slng) && Math.abs(slat) <= 90 && Math.abs(slng) <= 180) {
          const ageBucket = getAgeBucket(getLocationAgeMs(status));
          out.push({ deviceId: id, lat: slat, lng: slng, ageBucket });
        }
      }
    }

    return out;
  }, [statusMap]);

  function toRadians(value: number): number {
    return (value * Math.PI) / 180;
  }

  function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const earthRadiusKm = 6371;
    const dLat = toRadians(lat2 - lat1);
    const dLng = toRadians(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2
      + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  }

  // Layout: viewport width, sidebar visible or not, and its current width (for resize).
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(() => window.innerWidth);

  // Refs for the resize handle: track drag state and starting position/width.
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(window.innerWidth);

  const [isToggling, setIsToggling] = useState(false);

  const { lat, lng, urlDeviceId } = useMemo(() => {
    const sp = new URLSearchParams(queryString);
    return {
      lat: parseFloat(sp.get('lat') || '0'),
      lng: parseFloat(sp.get('lng') || '0'),
      urlDeviceId: sp.get('deviceId') ?? undefined,
    };
  }, [queryString]);

  const center: LatLngTuple = useMemo(() => {
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      return [lat, lng];
    }
    return [51.5074, -0.1278];
  }, [lat, lng]);
  const hasCoords = lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng);

  useEffect(() => {
    if (urlDeviceId) setMapDeviceIdSearch(urlDeviceId);
  }, [urlDeviceId]);

  useEffect(() => {
    if (!hasCoords) return;
    setManualLat(String(lat));
    setManualLng(String(lng));
  }, [hasCoords, lat, lng]);

  const onCenterMapOnLocation = useMemo(
    () => (latVal: number, lngVal: number, deviceId?: string) => {
      applyMapSearchParams((p) => {
        p.set('lat', String(latVal));
        p.set('lng', String(lngVal));
        if (deviceId) p.set('deviceId', deviceId);
        else p.delete('deviceId');
      });
      setSidebarOpen(false);
    },
    [applyMapSearchParams],
  );

  async function applyNetworkCenter(): Promise<void> {
    setNetworkLocateBusy(true);
    try {
      const location = await resolveApproximateNetworkLocation();
      const latVal = location.lat;
      const lngVal = location.lng;
      setRadiusCenter({ lat: latVal, lng: lngVal });
      setRadiusCenterSource('network');
      setRadiusFilterEnabled(true);
      setManualLat(String(latVal));
      setManualLng(String(lngVal));
      applyMapSearchParams((p) => {
        p.set('lat', String(latVal));
        p.set('lng', String(lngVal));
        p.delete('deviceId');
      });
      window.dispatchEvent(
        new CustomEvent('toast-info', {
          detail: 'Radius center set from network location (IP-based, approximate).',
        }),
      );
    } catch (err: any) {
      showToastError(err, 'Could not resolve network location');
    } finally {
      setNetworkLocateBusy(false);
    }
  }

  function applyManualLocation(): void {
    const latVal = Number(manualLat.trim());
    const lngVal = Number(manualLng.trim());
    if (!Number.isFinite(latVal) || !Number.isFinite(lngVal) || Math.abs(latVal) > 90 || Math.abs(lngVal) > 180) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Enter valid latitude/longitude values' }));
      return;
    }
    setRadiusCenter({ lat: latVal, lng: lngVal });
    setRadiusCenterSource('manual');
    setRadiusFilterEnabled(true);
    applyMapSearchParams((p) => {
      p.set('lat', String(latVal));
      p.set('lng', String(lngVal));
      p.delete('deviceId');
    });
  }

  const scopedMarkers = useMemo(() => {
    if (!Array.isArray(baseVisibleDeviceIds) || baseVisibleDeviceIds.length === 0) return deviceMarkers;
    const allowed = new Set(baseVisibleDeviceIds);
    return deviceMarkers.filter((m) => allowed.has(m.deviceId));
  }, [deviceMarkers, baseVisibleDeviceIds]);

  const markersAfterIdSearch = useMemo(() => {
    const q = mapDeviceIdSearch.trim().toLowerCase();
    if (!q) return scopedMarkers;
    return scopedMarkers.filter((m) => m.deviceId.toLowerCase().includes(q));
  }, [scopedMarkers, mapDeviceIdSearch]);

  const markersAfterStatusFilter = useMemo(() => {
    if (visibleAgeBuckets.length === 0) return [];
    const allowed = new Set(visibleAgeBuckets);
    return markersAfterIdSearch.filter((m) => allowed.has(m.ageBucket));
  }, [markersAfterIdSearch, visibleAgeBuckets]);

  /** When a device id filter is active, do not clip markers to the radius ring so Focus / search can show distant matches. */
  const effectiveMarkers = useMemo(() => {
    if (mapDeviceIdSearch.trim()) return markersAfterStatusFilter;
    if (!radiusFilterEnabled || !radiusCenter) return markersAfterStatusFilter;
    return markersAfterStatusFilter.filter((m) => haversineKm(radiusCenter.lat, radiusCenter.lng, m.lat, m.lng) <= radiusKm);
  }, [markersAfterStatusFilter, radiusCenter, radiusFilterEnabled, radiusKm, mapDeviceIdSearch]);

  function focusFirstMatchingDevice(): void {
    const q = mapDeviceIdSearch.trim().toLowerCase();
    if (!q) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Enter part of a device id to focus on the map' }));
      return;
    }
    const matches = scopedMarkers.filter((m) => m.deviceId.toLowerCase().includes(q));
    if (matches.length === 0) {
      window.dispatchEvent(
        new CustomEvent('toast-error', { detail: 'No device with a map position in the current scope matches that id' }),
      );
      return;
    }
    const m = matches[0];
    flyToNonceRef.current += 1;
    setFlyTo({ position: [m.lat, m.lng], nonce: flyToNonceRef.current });
    if (matches.length > 1) {
      window.dispatchEvent(
        new CustomEvent('toast-info', {
          detail: `${matches.length} devices match; map centered on ${m.deviceId}`,
        }),
      );
    }
  }

  const radiusLabel = radiusKm < 1 ? `${Math.round(radiusKm * 1000)}m` : `${radiusKm.toFixed(1)}km`;
  const radiusSliderValue = useMemo(() => radiusKmToSliderValue(radiusKm), [radiusKm]);

  // When URL has coords (e.g. “find me”), collapse sidebar so the map is visible.
  useEffect(() => {
    if (hasCoords) setSidebarOpen(false);
  }, [hasCoords]);

  // Keep viewport and sidebar width in sync on window resize; clamp sidebar to min/max.
  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setSidebarWidth((w) =>
        Math.min(Math.max(SIDEBAR_MIN_WIDTH, w), window.innerWidth),
      );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Resize handle: drag to change sidebar width; notify device list to collapse/expand filters.
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !sidebarOpen) return;

      const delta = e.clientX - dragStartX.current;
      if (!hasDragged.current && Math.abs(delta) < DRAG_THRESHOLD_PX) return;
      hasDragged.current = true;

      const baseWidth = dragStartWidth.current;
      const maxWidth = viewportWidth;
      const next = Math.min(
        maxWidth,
        Math.max(SIDEBAR_MIN_WIDTH, baseWidth + delta),
      );
      setSidebarWidth(next);

      const ratio = next / maxWidth;
      if (ratio < 0.5) {
        window.dispatchEvent(new Event('device-filters-collapse'));
      } else {
        window.dispatchEvent(new Event('device-filters-expand'));
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      hasDragged.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sidebarOpen, viewportWidth]);

  // Position the "Devices" toggle so it sits just right of the sidebar (or left when collapsed).
  const buttonLeft = sidebarOpen
    ? Math.min(sidebarWidth + 6, viewportWidth - 80)
    : 12;

  function handleToggleSidebar() {
    setSidebarOpen((open) => !open);
    setIsToggling(true);
    setTimeout(() => setIsToggling(false), 300);
  }

  const rootClass =
    variant === 'dock'
      ? 'relative h-full min-h-0 w-full overflow-hidden bg-gray-100 dark:bg-slate-800'
      : 'relative h-[calc(100vh-60px-60px)] w-full overflow-hidden bg-gray-100 dark:bg-slate-800';

  return (
    <div className={rootClass}>
      {variant === 'page' && (
        <>
          {/* Sidebar – device list + resizable edge */}
          <div
            ref={sidebarRef}
            className={`absolute left-0 top-0 bottom-0 z-10 flex bg-white dark:bg-slate-900 border-r shadow-sm transition-[width] duration-150 ease-out overflow-hidden ${sidebarOpen ? 'p-2' : 'p-0'
              }`}
            style={{ width: sidebarOpen ? `${sidebarWidth}px` : '0px' }}
          >
            {sidebarOpen && (
              <>
                <div className="h-full overflow-y-auto w-full">
                  <DeviceList
                    onCenterMapOnLocation={onCenterMapOnLocation}
                    operatorScopedEventId={operatorScopedEventId}
                    operatorScopedEventName={operatorScopedEventName}
                    operatorScopedRaceId={operatorScopedRaceId}
                    mapRadiusFilter={
                      radiusFilterEnabled && radiusCenter
                        ? {
                          centerLat: radiusCenter.lat,
                          centerLng: radiusCenter.lng,
                          radiusKm,
                        }
                        : null
                    }
                    onClearMapRadiusFilter={() => {
                      setRadiusFilterEnabled(false);
                      setRadiusCenter(null);
                    }}
                  />
                </div>
                <div
                  className="w-[8px] cursor-col-resize bg-gradient-to-r from-gray-200 to-gray-400 hover:from-gray-300 hover:to-gray-500 transition-colors"
                  onMouseDown={(e) => {
                    if (e.button !== 0) return;
                    e.preventDefault();
                    isDragging.current = true;
                    hasDragged.current = false;
                    dragStartX.current = e.clientX;
                    dragStartWidth.current =
                      sidebarRef.current?.getBoundingClientRect().width ?? sidebarWidth;
                  }}
                />
              </>
            )}
          </div>

          {/* Toggle button – show/hide device list */}
          <div
            className="absolute top-1/2 transform -translate-y-1/2 z-[999]"
            style={{ left: `${buttonLeft}px` }}
          >
            <button
              type="button"
              className={`rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-300 shadow-sm backdrop-blur-sm transition motion-safe:duration-200 motion-safe:hover:scale-[1.02] motion-safe:hover:border-blue-200 dark:border-blue-700/60 motion-safe:hover:shadow-md motion-safe:hover:bg-white dark:bg-slate-900 ${isToggling ? 'motion-safe:animate-bounce' : ''
                }`}
              onClick={handleToggleSidebar}
            >
              Devices
            </button>
          </div>
        </>
      )}

      <div className="absolute top-3 right-3 z-[1000] w-[min(22rem,92vw)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl ring-1 ring-gray-200/80 motion-safe:transition motion-safe:duration-300 motion-safe:hover:shadow-2xl dark:border-slate-600 dark:bg-slate-900 dark:ring-slate-700 dark:shadow-black/40">
        <button
          type="button"
          onClick={() => setMapControlsOpen((o) => !o)}
          aria-expanded={mapControlsOpen}
          className="flex w-full items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-left transition hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform motion-safe:duration-200 dark:text-slate-400 ${mapControlsOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
          <span className="text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-slate-200">Map controls</span>
          <span className="ml-auto tabular-nums text-[10px] font-semibold text-gray-500 dark:text-slate-400">
            {effectiveMarkers.length}/{markersAfterIdSearch.length}
          </span>
        </button>
        {mapControlsOpen ? (
          <div className="space-y-3 p-3.5 pt-3">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-2 text-[10px] font-semibold text-gray-800 dark:text-slate-200">Device ID</div>
          <div className="flex gap-2">
            <input
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              spellCheck={false}
              placeholder="e.g. Rear_1138"
              className="min-w-0 flex-1 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-[11px] text-gray-800 dark:text-slate-200 shadow-inner transition placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              value={mapDeviceIdSearch}
              onChange={(e) => setMapDeviceIdSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  focusFirstMatchingDevice();
                }
              }}
            />
            <button
              type="button"
              className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-700/60 dark:bg-blue-950/40 px-2.5 py-1.5 text-[11px] font-semibold text-blue-900 shadow-sm transition hover:bg-blue-100"
              onClick={focusFirstMatchingDevice}
            >
              Focus
            </button>
          </div>
          {mapDeviceIdSearch.trim() ? (
            <div className="mt-1.5 text-[9px] text-gray-500 dark:text-slate-400">
              {markersAfterIdSearch.length} of {scopedMarkers.length} with position match this filter
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400">Nearby radius</div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-gray-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-slate-300 transition hover:bg-gray-200 dark:hover:bg-slate-700">
            <input
              type="checkbox"
              checked={radiusFilterEnabled}
              onChange={(e) => setRadiusFilterEnabled(e.target.checked)}
              disabled={!radiusCenter}
              className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
            />
            Filter on
          </label>
        </div>

        <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-slate-700 dark:bg-slate-800">
          {radiusCenter ? (
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">Circle center</div>
                  {radiusCenterSource ? (
                    <span
                      className={`mt-1 inline-block rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                        radiusCenterSource === 'network'
                          ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/80'
                          : 'bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-slate-200 ring-1 ring-gray-300 dark:ring-slate-600'
                      }`}
                    >
                      {radiusCenterSource === 'network' ? 'IP estimate' : 'Manual'}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:text-slate-300 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-800"
                  onClick={() => {
                    setRadiusCenter(null);
                    setRadiusFilterEnabled(false);
                  }}
                >
                  Clear
                </button>
              </div>
              <div
                className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-2.5 py-2 font-mono text-[11px] leading-snug text-gray-800 dark:text-slate-200 break-all"
                title={`${radiusCenter.lat}, ${radiusCenter.lng}`}
              >
                {radiusCenter.lat.toFixed(6)}, {radiusCenter.lng.toFixed(6)}
              </div>
            </div>
          ) : (
            <p className="text-[10px] leading-snug text-gray-600 dark:text-slate-300">
              Set a center with <span className="font-semibold text-gray-800 dark:text-slate-200">network location</span> (no permission) or{' '}
              <span className="font-semibold text-gray-800 dark:text-slate-200">coordinates</span>, then turn the filter on.
            </p>
          )}

          <div className="space-y-3 border-t border-gray-200 dark:border-slate-700 pt-3">
            <div>
              <div className="mb-2 text-[10px] font-semibold text-gray-800 dark:text-slate-200">Network location</div>
              <button
                type="button"
                className="w-full overflow-hidden rounded-lg border border-amber-200/90 bg-gradient-to-r from-amber-50 to-orange-50/90 px-3 py-2 text-center text-[11px] font-semibold text-amber-950 shadow-sm transition hover:border-amber-300 hover:shadow disabled:opacity-50"
                onClick={() => void applyNetworkCenter()}
                disabled={networkLocateBusy}
              >
                {networkLocateBusy ? 'Looking up…' : 'Use network location'}
              </button>
            </div>

            <div>
              <div className="mb-1 text-[10px] font-semibold text-gray-800 dark:text-slate-200">Coordinates</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  placeholder="Latitude"
                  className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-[11px] text-gray-800 dark:text-slate-200 shadow-inner transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={manualLat}
                  onChange={(e) => setManualLat(e.target.value)}
                />
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  placeholder="Longitude"
                  className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-[11px] text-gray-800 dark:text-slate-200 shadow-inner transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={manualLng}
                  onChange={(e) => setManualLng(e.target.value)}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-gray-800 dark:text-slate-200 shadow-sm transition hover:bg-gray-50 dark:hover:bg-slate-800"
                  onClick={applyManualLocation}
                >
                  Apply coordinates
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={RADIUS_SLIDER_STEPS}
            step={1}
            value={radiusSliderValue}
            onChange={(e) => setRadiusKm(sliderValueToRadiusKm(Number(e.target.value)))}
            disabled={!radiusCenter}
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-gray-200 dark:bg-slate-700 accent-blue-600 transition motion-safe:hover:bg-slate-200 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md"
          />
          <div className="w-[3.25rem] shrink-0 text-right text-[11px] font-bold tabular-nums text-gray-800 dark:text-slate-200">{radiusLabel}</div>
        </div>
        <details className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/45 px-2.5 py-2" open>
          <summary className="cursor-pointer select-none list-none text-[10px] font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400 [&::-webkit-details-marker]:hidden">
            Marker colors (location age)
          </summary>
          <div className="mt-2 mb-1">
            <label className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-gray-700 dark:text-slate-300">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                checked={visibleAgeBuckets.length === LOCATION_AGE_LEGEND.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setVisibleAgeBuckets(LOCATION_AGE_LEGEND.map(({ bucket }) => bucket));
                  } else {
                    setVisibleAgeBuckets([]);
                  }
                }}
              />
              Select all
            </label>
          </div>
          <ul className="mt-2 grid gap-1.5">
            {LOCATION_AGE_LEGEND.map(({ bucket, title, range }) => (
              <li key={bucket} className="flex items-center gap-2 text-[10px] leading-tight text-gray-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                  checked={visibleAgeBuckets.includes(bucket)}
                  onChange={(e) => {
                    setVisibleAgeBuckets((prev) => {
                      if (e.target.checked) {
                        if (prev.includes(bucket)) return prev;
                        return [...prev, bucket];
                      }
                      return prev.filter((b) => b !== bucket);
                    });
                  }}
                />
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-gray-300 dark:ring-slate-500"
                  style={{ backgroundColor: markerColorForAgeBucket(bucket) }}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="font-semibold text-gray-800 dark:text-slate-200">{title}</span>
                  <span className="text-gray-500 dark:text-slate-400"> · {range}</span>
                </span>
              </li>
            ))}
          </ul>
        </details>
        <div className="mt-2 border-t border-gray-200 dark:border-slate-700 pt-2 text-[10px] text-gray-500 dark:text-slate-400">
          {effectiveMarkers.length} / {markersAfterIdSearch.length} devices on map
          {mapDeviceIdSearch.trim() && radiusFilterEnabled && radiusCenter ? ' · id search overrides radius for markers' : ''}
          {!mapDeviceIdSearch.trim() && radiusFilterEnabled && radiusCenter ? ' (after radius)' : ''}
        </div>
        {selectedDeviceIds.length > 0 && (
          <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-blue-100 dark:border-blue-900/60 bg-blue-50/80 px-2 py-1.5 text-[10px] text-blue-900">
            <span>
              {selectedDeviceIds.length} map-selected
            </span>
            <button
              type="button"
              className="rounded border border-blue-200 dark:border-blue-700/60 bg-white dark:bg-slate-900 px-1.5 py-0.5 font-medium hover:bg-blue-50"
              onClick={clearSelectedDeviceIds}
            >
              Clear
            </button>
          </div>
        )}
          </div>
        ) : null}
      </div>

      {/* Map – Mapbox tiles; URL marker (if any) + live device positions */}
      <div className="absolute inset-0 z-0">
        <MapContainer
          center={center}
          zoom={hasCoords ? 13 : deviceMarkers.length > 0 ? 10 : 6}
          scrollWheelZoom
          className="h-full w-full"
        >
          <TileLayer attribution={TILE_ATTRIBUTION} url={tileLayerUrl} />
          <InvalidateMapSize revision={variant === 'dock' ? layoutRevision : 0} />
          {!hasCoords && effectiveMarkers.length > 0 && (
            <FitBounds positions={effectiveMarkers.map((m) => [m.lat, m.lng] as LatLngTuple)} />
          )}
          {hasCoords && <SyncMapToUrlCenter center={[lat, lng]} />}
          {flyTo ? <FlyToPosition target={flyTo.position} nonce={flyTo.nonce} /> : null}
          {radiusFilterEnabled && radiusCenter && (
            <Circle
              center={[radiusCenter.lat, radiusCenter.lng]}
              radius={radiusKm * 1000}
              pathOptions={{ color: '#2563eb', weight: 2, fillColor: '#3b82f6', fillOpacity: 0.12 }}
            />
          )}
          {effectiveMarkers.map((m) => (
            <Marker
              key={m.deviceId}
              position={[m.lat, m.lng]}
              icon={buildAgeMarkerIcon(
                markerColorForAgeBucket(m.ageBucket),
                selectedDeviceIds.includes(m.deviceId),
              )}
            >
              <Popup>
                <strong>{m.deviceId}</strong>
                <br />
                Lat: {m.lat.toFixed(5)}, Lng: {m.lng.toFixed(5)}
                <br />
                <span className="text-[11px] text-gray-600 dark:text-slate-300">
                  Age: {ageBucketCaption(m.ageBucket)}
                </span>
                <br />
                <button
                  type="button"
                  className="mt-1 rounded border border-blue-200 bg-blue-50 dark:border-blue-700/60 dark:bg-blue-950/40 px-2 py-0.5 text-[11px] font-semibold text-blue-800 hover:bg-blue-100"
                  onClick={() => toggleSelectedDeviceId(m.deviceId)}
                >
                  {selectedDeviceIds.includes(m.deviceId) ? 'Unselect from list filter' : 'Select for list filter'}
                </button>
                {selectedDeviceIds.includes(m.deviceId) ? (
                  <>
                    <br />
                    <span className="text-[11px] font-semibold text-blue-700">Selected for device list filter</span>
                  </>
                ) : null}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
